import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../Header/header";
import Fotter from "../Fotter/fotter";
import { toast } from "react-toastify";
import API_URL from "../../config";


function RegisterUser() {
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Details
  const [form, setForm] = useState({ name: "", email: "", password: "", otp: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.msg);
        setStep(2);
      } else {
        toast.error(data.msg);
      }
    } catch (error) {
      toast.error("Failed to send OTP");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, otp: form.otp }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.msg);
        setStep(3);
      } else {
        toast.error(data.msg);
      }
    } catch (error) {
      toast.error("Failed to verify OTP");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Account created successfully!");
        navigate("/login");
      } else {
        toast.error(data.msg);
      }
    } catch (error) {
      toast.error("Registration failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Header />
      <div className="min-h-screen flex items-center justify-center bg-sky-50 p-6">
        <div className="w-full max-w-5xl flex flex-col md:flex-row bg-white rounded-xl shadow-md overflow-hidden">
          {/* Left panel - Restored Original UI */}
          <div className="w-full md:w-1/2 bg-sky-100 p-10 flex flex-col justify-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Create Your Account
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Join thousands of travelers exploring Sri Lanka
            </p>

            <div className="space-y-5">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">✈️</span>
                <span className="text-green-600 font-medium">
                  Exclusive travel deals
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-2xl">🏨</span>
                <span className="text-blue-600 font-medium">
                  Personalized recommendations
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <span className="text-2xl">⭐</span>
                <span className="text-orange-600 font-medium">
                  Reward points on bookings
                </span>
              </div>
            </div>

            {/* Step progress indicator */}
            <div className="mt-12 pt-8 border-t border-sky-200">
              <div className="flex items-center gap-2 mb-4">
                <div className={`h-2 flex-1 rounded-full ${step >= 1 ? 'bg-sky-500' : 'bg-gray-300'}`} />
                <div className={`h-2 flex-1 rounded-full ${step >= 2 ? 'bg-sky-500' : 'bg-gray-300'}`} />
                <div className={`h-2 flex-1 rounded-full ${step >= 3 ? 'bg-sky-500' : 'bg-gray-300'}`} />
              </div>
              <p className="text-sm font-semibold text-sky-700">
                {step === 1 && "Step 1: Email Verification"}
                {step === 2 && "Step 2: Enter OTP"}
                {step === 3 && "Step 3: Complete Profile"}
              </p>
            </div>
          </div>

          {/* Right panel (Forms) */}
          <div className="w-full md:w-1/2 p-10">
            {step === 1 && (
              <form onSubmit={handleSendOTP} className="space-y-6">
                <h2 className="text-2xl font-bold text-amber-600">Register</h2>
                <p className="text-sm text-gray-600">Sign up to start your journey</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Address</label>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="w-full mt-1 px-4 py-2 border border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-500 outline-none"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 transition duration-200"
                >
                  {isSubmitting ? "Sending OTP..." : "Get OTP →"}
                </button>
                <div className="text-sm text-gray-600 pt-4 flex flex-col gap-2">
                  <p>Already have an account? <Link to="/login" className="text-amber-600 font-medium hover:underline">Sign in</Link></p>
                  <p className="border-t border-gray-100 pt-2 text-xs">Are you an administrator? <Link to="/register-admin" className="text-blue-600 font-bold hover:underline">Register as Admin</Link></p>
                </div>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={handleVerifyOTP} className="space-y-6">
                <h2 className="text-2xl font-bold text-amber-600">Verify OTP</h2>
                <p className="text-sm text-gray-600">Enter the 6-digit code sent to {form.email}</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700">6-Digit Code</label>
                  <input
                    type="text"
                    maxLength="6"
                    placeholder="000000"
                    className="w-full mt-1 px-4 py-2 border border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-500 text-center text-2xl tracking-widest outline-none"
                    value={form.otp}
                    onChange={(e) => setForm({ ...form, otp: e.target.value })}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 transition duration-200"
                >
                  {isSubmitting ? "Verifying..." : "Verify OTP →"}
                </button>
                <button type="button" onClick={() => setStep(1)} className="text-sm text-gray-500 hover:underline w-full text-center">
                  Back to email
                </button>
              </form>
            )}

            {step === 3 && (
              <form onSubmit={handleRegister} className="space-y-6">
                <h2 className="text-2xl font-bold text-amber-600">Complete Profile</h2>
                <p className="text-sm text-gray-600">Last step to join Travel Aura</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Full Name</label>
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    className="w-full mt-1 px-4 py-2 border border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-500 outline-none"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    placeholder="Create a password"
                    className="w-full mt-1 px-4 py-2 border border-sky-200 rounded-lg focus:ring-2 focus:ring-sky-500 outline-none"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-teal-500 to-emerald-500 hover:opacity-90 transition duration-200"
                >
                  {isSubmitting ? "Finishing..." : "Create Account →"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      <Fotter />
    </div>
  );
}

export default RegisterUser;